import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Eye, ShieldWarning, Bug, Lightbulb, ChartLineUp, PlugsConnected,
  MagnifyingGlass, ChatCircleDots, ArrowRight, Lightning,
} from "@phosphor-icons/react";
import { getIndustry } from "@/lib/industries";
import { AIAssistant } from "./_ai";

const LENS_DEFS = [
  { key: "assumption", title: "Assumption Lens", desc: "Validate team assumptions in real time using AI and NLP", icon: Eye, color: "#0020F5", bg: "#E5EAFF" },
  { key: "risk", title: "Risk Lens", desc: "Visualize risk with heatmaps and forecasts", icon: ShieldWarning, color: "#FF3B30", bg: "#FFE5E3" },
  { key: "issue", title: "Issue Lens", desc: "Detect and classify issues early with root cause analysis", icon: Bug, color: "#FF9500", bg: "#FFEFD6" },
  { key: "solution", title: "Solution Lens", desc: "Generate actionable recommendations based on AI reasoning", icon: Lightbulb, color: "#00C7BE", bg: "#D6F7F4" },
  { key: "insights", title: "Insights Lens", desc: "Aggregate key insights across lenses and show trends", icon: ChartLineUp, color: "#7C3AED", bg: "#EDE4FF" },
  { key: "integration", title: "Integration Lens", desc: "Ingest project data from CSV, Excel, PDF, Azure DevOps and MS Project.", icon: PlugsConnected, color: "#0020F5", bg: "#E5EAFF" },
];

const HEALTH_TONE = (n) => n >= 70 ? { label: "Good", color: "#16A34A", bg: "#DCFCE7" } : n >= 40 ? { label: "Watch", color: "#FF9500", bg: "#FFEFD6" } : { label: "At Risk", color: "#FF3B30", bg: "#FFE5E3" };

export default function ProjectOverview({ projectId }) {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState({
    assumptions: 0, assumptions_high_risk: 0,
    risks_total: 0, risks_critical: 0,
    issues_total: 0, issues_pending: 0,
    solutions_total: 0, solutions_implemented: 0,
    insights_alerts: 0, insights_trends: 0,
    integrations_total: 0,
    health: 0,
  });
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [pRes, aRes, rRes, iRes, sRes, intRes, statsRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/assumptions`),
        api.get(`/projects/${projectId}/risks`),
        api.get(`/projects/${projectId}/issues`),
        api.get(`/projects/${projectId}/solutions`),
        api.get(`/projects/${projectId}/integrations`),
        api.get(`/projects/${projectId}/stats`),
      ]);
      const a = aRes.data, r = rRes.data, is = iRes.data, s = sRes.data, integ = intRes.data;
      setProject(pRes.data);
      const assumptions_high_risk = a.filter(x => x.validity_score != null && x.validity_score < 50).length;
      const risks_critical = r.filter(x => x.severity === "critical" || x.severity === "high").length;
      const issues_pending = is.filter(x => x.status !== "resolved").length;
      // Health is the single canonical server-side value — same number used by Insights Lens.
      const health = statsRes.data?.health_score ?? 0;
      setStats({
        assumptions: a.length, assumptions_high_risk,
        risks_total: r.length, risks_critical,
        issues_total: is.length, issues_pending,
        solutions_total: s.length, solutions_implemented: s.length,
        insights_alerts: risks_critical + issues_pending,
        insights_trends: Math.max(0, a.filter(x => x.validity_score != null).length),
        integrations_total: integ.length,
        health,
      });
    } catch {
      toast.error("Failed to load overview");
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const healthTone = HEALTH_TONE(stats.health);

  const sideStats = [
    { key: "assumption", label: "Assumption Lens", sub: `${stats.assumptions} assumptions`, icon: Eye, color: "#0020F5", bg: "#E5EAFF" },
    { key: "risk", label: "Risk Lens", sub: `${stats.risks_critical} critical risks`, icon: ShieldWarning, color: "#FF3B30", bg: "#FFE5E3" },
    { key: "issue", label: "Issue Lens", sub: `${stats.issues_pending} pending issues`, icon: Bug, color: "#FF9500", bg: "#FFEFD6" },
    { key: "solution", label: "Solution Lens", sub: `${stats.solutions_total} generated`, icon: Lightbulb, color: "#00C7BE", bg: "#D6F7F4" },
    { key: "insights", label: "Insights Lens", sub: `${stats.insights_trends} active trends`, icon: ChartLineUp, color: "#7C3AED", bg: "#EDE4FF" },
    { key: "integration", label: "Integration Lens", sub: `${stats.integrations_total} data sources`, icon: PlugsConnected, color: "#0020F5", bg: "#E5EAFF" },
  ];

  return (
    <div className="grid grid-cols-12 gap-6" data-testid="project-overview">
      {/* LEFT BLUE PANEL */}
      <aside className="col-span-12 lg:col-span-3 bg-[#0020F5] text-white p-6 min-h-[calc(100vh-120px)] rounded-none" data-testid="overview-lens-panel">
        <div className="flex items-center gap-2 text-white/90">
          <Eye size={20} weight="fill" />
          <h3 className="font-display font-bold text-lg">ARISSAI Lenses</h3>
        </div>

        {/* Overview card (active) */}
        <button
          className="mt-5 w-full bg-white/15 backdrop-blur-sm border border-white/30 p-4 text-left hover:bg-white/20 transition-colors flex items-center justify-between"
          data-testid="overview-active-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/25 grid place-items-center">
              <Eye size={18} weight="duotone" />
            </div>
            <div>
              <div className="font-display font-bold text-sm">Overview</div>
              <div className="text-[11px] text-white/70">All lenses summary</div>
            </div>
          </div>
          <ArrowRight size={14} weight="bold" />
        </button>

        <div className="h-px bg-white/20 my-5" />

        {/* Lens stat rows */}
        <div className="space-y-3">
          {sideStats.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => navigate(`/projects/${projectId}/${s.key}`)}
                className="w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors text-left"
                data-testid={`side-lens-${s.key}`}
              >
                <div className="w-10 h-10 grid place-items-center shrink-0" style={{ background: s.bg }}>
                  <Icon size={18} weight="duotone" style={{ color: s.color }} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{s.label}</div>
                  <div className="text-[11px] text-white/70 truncate">{s.sub}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="h-px bg-white/20 my-5" />

        {/* Quick stats */}
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/60">Quick Stats</div>
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/90">{getIndustry(project?.industry).kpiLabel}</span>
              <span className="px-2 h-6 inline-flex items-center text-[11px] font-bold uppercase tracking-wider" style={{ background: healthTone.bg, color: healthTone.color }} data-testid="overview-health-pill">{healthTone.label}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/90">Total Items</span>
              <span className="font-mono text-sm" data-testid="overview-total-items">{stats.assumptions + stats.risks_total + stats.issues_total}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/90">Health Score</span>
              <span className="font-mono text-sm" data-testid="overview-health-score">{stats.health}/100</span>
            </div>
          </div>
        </div>
      </aside>

      {/* CENTER CONTENT */}
      <section className="col-span-12 lg:col-span-6" data-testid="overview-center">
        <div className="text-center mb-10">
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">See Your Project's Truth—One Lens at a Time</h2>
          <p className="text-zinc-600 max-w-2xl mx-auto mt-3 text-sm sm:text-base leading-relaxed">
            ARISSAI helps you make better decisions by analyzing assumptions, identifying risks, surfacing issues, and generating solutions through six intelligent lenses.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {LENS_DEFS.map((l) => {
            const Icon = l.icon;
            const meta = l.key === "assumption"
              ? { primary: stats.assumptions, primaryLabel: "Total Assumptions", chip: `${stats.assumptions_high_risk} High Risk`, chipColor: "#FF3B30", chipBg: "#FFE5E3" }
              : l.key === "risk"
              ? { primary: stats.risks_total, primaryLabel: "Total Risks", chip: `${stats.risks_critical} Critical`, chipColor: "#FF3B30", chipBg: "#FFE5E3" }
              : l.key === "issue"
              ? { primary: stats.issues_total, primaryLabel: "Total Issues", chip: `${stats.issues_pending} Pending`, chipColor: "#52525B", chipBg: "#F4F4F5" }
              : l.key === "solution"
              ? { primary: stats.solutions_total, primaryLabel: "Solutions", chip: `${stats.solutions_implemented} Generated`, chipColor: "#16A34A", chipBg: "#DCFCE7" }
              : l.key === "insights"
              ? { primary: stats.insights_trends, primaryLabel: "Active Trends", chip: `${stats.insights_alerts} Alerts`, chipColor: "#7C3AED", chipBg: "#EDE4FF" }
              : { primary: stats.integrations_total, primaryLabel: "Data Sources", chip: stats.integrations_total > 0 ? "Last sync: now" : "Not synced", chipColor: "#52525B", chipBg: "#F4F4F5" };

            return (
              <button
                key={l.key}
                onClick={() => navigate(`/projects/${projectId}/${l.key}`)}
                className="text-left bg-white border border-zinc-200 p-6 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.15)] transition-all"
                data-testid={`overview-lens-card-${l.key}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 grid place-items-center shrink-0" style={{ background: l.bg }}>
                    <Icon size={22} weight="duotone" style={{ color: l.color }} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-display font-bold text-lg leading-tight">{l.title}</h4>
                    <p className="text-sm text-zinc-600 mt-1 leading-snug">{l.desc}</p>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-zinc-100 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500">{meta.primaryLabel}</div>
                    <div className="font-display font-black text-3xl mt-1">{meta.primary}</div>
                  </div>
                  <span className="px-2.5 h-7 inline-flex items-center text-[11px] font-bold" style={{ background: meta.chipBg, color: meta.chipColor }}>
                    {meta.chip}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* RIGHT DARK PANEL */}
      <aside className="col-span-12 lg:col-span-3 bg-[#0A0F1E] text-white p-6 min-h-[calc(100vh-120px)]" data-testid="overview-right-panel">
        {/* Search */}
        <div>
          <div className="flex items-center gap-2">
            <MagnifyingGlass size={18} weight="bold" />
            <h3 className="font-display font-bold text-base">Search & Discover</h3>
          </div>
          <div className="mt-3 relative">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search across all project data..."
              className="w-full h-11 pl-4 pr-10 bg-white/5 border border-white/15 text-sm placeholder:text-white/40 text-white focus:outline-none focus:border-white/40"
              data-testid="overview-search-input"
            />
            <MagnifyingGlass size={16} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50" />
          </div>
        </div>

        <div className="h-px bg-white/10 my-6" />

        {/* Data sources summary — read-only. Manage data in the Integration Lens. */}
        <div>
          <div className="flex items-center gap-2">
            <PlugsConnected size={18} weight="bold" />
            <h3 className="font-display font-bold text-base">Data Sources</h3>
          </div>
          <div className="mt-3 bg-white/5 border border-white/10 p-4">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-display font-black text-3xl text-white" data-testid="overview-integrations-count">{stats.integrations_total}</div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-white/60 mt-1">Files imported</div>
              </div>
              <div className="text-right text-xs text-white/70 leading-relaxed">
                CSV · Excel · PDF<br />
                <span className="text-white/40">classified across lenses</span>
              </div>
            </div>
            <button
              onClick={() => navigate(`/projects/${projectId}/integration`)}
              className="mt-4 w-full h-10 inline-flex items-center justify-center gap-2 bg-white text-zinc-950 text-xs font-bold tracking-[0.1em] uppercase hover:bg-white/90 transition-colors"
              data-testid="overview-go-integration-lens"
            >
              Manage in Integration Lens <ArrowRight size={14} weight="bold" />
            </button>
            <p className="mt-3 text-[11px] text-white/50 leading-relaxed">
              Upload, review and remove data sources in the Integration Lens — that's where every import is validated row-by-row before it populates the other lenses.
            </p>
          </div>
        </div>

        <div className="h-px bg-white/10 my-6" />

        {/* AI Assistant */}
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChatCircleDots size={18} weight="bold" />
              <h3 className="font-display font-bold text-base">AI Assistant</h3>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2 h-6 bg-[#16A34A]/20 text-[#22D27D] text-[10px] font-bold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 bg-[#22D27D] rounded-full" /> Online
            </span>
          </div>
          <div className="mt-3 bg-white/5 border border-white/10 p-3 text-xs text-white/80 leading-relaxed">
            <Lightning size={14} weight="fill" className="inline -mt-0.5 mr-1 text-[#FFD400]" />
            Tip: head to the <button onClick={() => navigate(`/projects/${projectId}/integration`)} className="font-bold text-white underline underline-offset-2 hover:text-white/80">Integration Lens</button> to upload data — every row is reviewed before it lands across the lenses.
          </div>
        </div>
      </aside>
      <AIAssistant projectId={projectId} lens="overview" label="Overview" />
    </div>
  );
}
